/**
 * The P5 re-ranker (docs/proposals/ai-personas-voice-and-sound.md §Playing
 * styles): "choose among the moves within a small centipawn window of the
 * best, ranked by a style score… Strength still comes from Elo/skill; the
 * window is how far a style may stray from best."
 *
 * Properties, each pinned by tests:
 * - **The window is the strength bound.** Only candidates within
 *   `STYLE_WINDOW_CHESS` / `STYLE_WINDOW_CHECKERS` centipawns of the best are
 *   eligible; a forced mate (±1e6 in both engines) therefore cannot be left,
 *   because every non-mate falls outside the window.
 * - **Legality is inherited** — candidates come from the engine's legal top-N,
 *   and a candidate whose move fails to apply is skipped, never thrown.
 * - **Deterministic**: no RNG anywhere; exact ties keep engine order (the
 *   sort is stable and falls back to scoreCp then input index).
 * - **Absent style = today's behaviour**: callers only enter this function
 *   when a style is active; `styleMovetimeMs` likewise returns the level's
 *   raw movetime untouched when not styled (see game-store wiring).
 *
 * `StyleAxes` is structurally the persona package's `PlayStyle` minus its id —
 * engines consume plain numbers and never import `@chessmate/persona`.
 */
import type { PositionSnapshot } from '@chessmate/protocol';
import type { BestMove } from '../engine.js';
import type { GameAdapter } from '../game-adapter.js';
import { extractFeatures, type StyleFeatures } from './features.js';

export interface StyleAxes {
  readonly aggression: number;
  readonly caution: number;
  readonly complexity: number;
  readonly tempo: number;
}

/** MultiPV candidate count when a style is active (API clamps MultiPV ≤ 5). */
export const STYLE_MULTI_PV = 4;

/**
 * Centipawn windows — the calibration ladder's shrink knob, and the one place
 * chess and checkers legitimately differ:
 *
 * - **Checkers 50** (half a man; man 100 / king 300): the engine's scores are
 *   QUANTIZED to man units, so a <100 window already admits only exact ties —
 *   window 0 and window 50 play identically (measured: byte-identical results
 *   over 150 games/style). 50 documents the intent (never trade a man).
 * - **Chess 0 — ties only, measured down from the spec's example 40.** On a
 *   continuous centipawn scale the window is a real strength cost:
 *   ~half the styled plies diverge × ~window/2 average penalty ≈ −5 pawns per
 *   game, which loses almost everything at kitten strength. Ladder evidence
 *   (2026-10-23, 60 ms kitten, n=30/style): window 40 → 0.267, window 10 →
 *   0.233, **window 0 → 0.433/0.450 (pooled 0.442 at n=60, statistically
 *   neutral), divergence 5.5–7.7%**. Spec-sanctioned loosening ("dropped or
 *   loosened, not shipped"): style now breaks ties the ENGINE cannot order —
 *   it never picks a strictly worse move.
 */
export const STYLE_WINDOW_CHESS = 0;
export const STYLE_WINDOW_CHECKERS = 50;

/** MultiPV splits the fixed movetime budget across lines; 1.2× compensates. Calibration knob. */
export const STYLE_MOVETIME_BUMP = 1.2;

/** Style-score weights — named because they are calibration knobs (spec §Budgets). */
export const W_AGG_CAPTURE = 0.45;
export const W_AGG_CHECK = 0.35;
export const W_AGG_ADVANCE = 0.2;
export const W_CAU_DEFENDED = 0.4;
export const W_CAU_SAFE = 0.3;
export const W_CAU_TRADE = 0.3;
/**
 * Damps the complexity axis so it breaks ties toward tension instead of
 * dominating the score. Without it, `complexity × (1 − capture)` spans the full
 * 0..1 range while an aggressive profile's capture term tops out at
 * 0.9 × 0.45 = 0.405 — an "aggressive" persona would rank quiet moves ABOVE
 * captures (the directional tests failed exactly this way before this knob).
 */
export const W_COMPLEXITY = 0.5;

export function styleWindowCp(gameId: string): number {
  return gameId === 'checkers' ? STYLE_WINDOW_CHECKERS : STYLE_WINDOW_CHESS;
}

/**
 * The style preference for one candidate. `tempo` is deliberately absent —
 * it is a movetime lever, not a move preference.
 */
export function styleScore(f: StyleFeatures, style: StyleAxes): number {
  const aggressive = W_AGG_CAPTURE * f.capture01 + W_AGG_CHECK * f.givesCheck + W_AGG_ADVANCE * f.advance01;
  const cautious = W_CAU_DEFENDED * f.defended + W_CAU_SAFE * f.safe + W_CAU_TRADE * f.tradeAhead;
  return style.aggression * aggressive + style.caution * cautious + W_COMPLEXITY * style.complexity * (1 - f.capture01);
}

/**
 * Re-rank the engine's top-N within the centipawn window by style score.
 * Returns the engine's best move unchanged when the window yields nothing
 * (defensive: the best candidate always qualifies).
 */
export function pickStyledMove(
  adapter: GameAdapter,
  pos: PositionSnapshot,
  candidates: readonly BestMove[],
  style: StyleAxes,
  /** Override the per-game default — the calibration ladder's shrink knob. */
  windowCp: number = styleWindowCp(adapter.gameId),
): BestMove {
  const first = candidates[0];
  if (!first) throw new Error('no move');
  let bestScore = Number.NEGATIVE_INFINITY;
  for (const c of candidates) if (Number.isFinite(c.scoreCp) && c.scoreCp > bestScore) bestScore = c.scoreCp;
  const window = windowCp;

  const pool: Array<{ candidate: BestMove; index: number; score: number }> = [];
  candidates.forEach((candidate, index) => {
    if (!Number.isFinite(candidate.scoreCp) || bestScore - candidate.scoreCp > window) return;
    const features = extractFeatures(adapter, pos, candidate.uci);
    if (features == null) return;
    pool.push({ candidate, index, score: styleScore(features, style) });
  });
  if (pool.length === 0) return first;

  pool.sort((a, b) => b.score - a.score || b.candidate.scoreCp - a.candidate.scoreCp || a.index - b.index);
  return pool[0]!.candidate;
}

/**
 * Movetime for a styled chess move: the level's budget, scaled by the style's
 * tempo (0.9–1.1×) and the MultiPV dilution bump, clamped to the API's own
 * 20–5000 ms envelope. Checkers ignores movetime entirely (depth-based), so
 * this is a chess-only lever in practice.
 */
export function styleMovetimeMs(level: { readonly movetimeMs: number }, style: StyleAxes): number {
  const scaled = level.movetimeMs * (0.9 + 0.2 * style.tempo) * STYLE_MOVETIME_BUMP;
  return Math.round(Math.min(5000, Math.max(20, scaled)));
}
